import { useQuery } from '@tanstack/react-query';
import { Filter, Search } from 'lucide-react';
import maplibregl from 'maplibre-gl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import 'maplibre-gl/dist/maplibre-gl.css';

import { Badge, Button, Input } from '@/components/ui';
import { kgApi } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { MapEntity } from '@/types/api';

import { MapClusterPopup } from './components/MapClusterPopup';
import { MapFilterPanel, type MapFilters, EMPTY_FILTERS } from './components/MapFilterPanel';
import { MapPopupContent } from './components/MapPopupContent';

// Guard against malformed env values (e.g. a leaked dotenv comment).
// A real MapTiler key is alphanumeric and ~16-32 chars long.
const MAPTILER_KEY = (import.meta.env.VITE_MAPTILER_API_KEY ?? '').trim();
const KEY_LOOKS_VALID = /^[A-Za-z0-9_-]{8,}$/.test(MAPTILER_KEY);
const STYLE_URL = KEY_LOOKS_VALID
  ? `https://api.maptiler.com/maps/streets-v2/style.json?key=${MAPTILER_KEY}`
  : 'https://demotiles.maplibre.org/style.json';

/**
 * Interactive Map (Spec S05). Phase 6 ships the canvas, the filter overlay
 * and the search bar. Entity markers + info-bubbles wire to the Layer 4 SPARQL
 * endpoints once their map-mode response is implemented (post-MVP polish).
 */
export function MapPage() {
  const { t } = useTranslation('app');
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markersRef = useRef<maplibregl.Marker[]>([]);
  const popupRef = useRef<maplibregl.Popup | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState<MapFilters>(EMPTY_FILTERS);
  const [mapReady, setMapReady] = useState(false);
  // Memoised div that maplibre's popup owns DOM-wise, and that we
  // populate via createPortal so the React tree (QueryClient,
  // Router, theme) stays in scope inside the popup.
  const [popupHost] = useState<HTMLDivElement>(() => {
    const el = document.createElement('div');
    el.className = 'ett-map-popup-host';
    return el;
  });
  // `active` covers two shapes:
  //   - `kind: 'single'` — one entity, optionally with a `siblings` list
  //     when the analyst drilled in from a cluster (enables Back).
  //   - `kind: 'group'` — multiple entities at the same coord; the
  //     popup renders the picker.
  const [active, setActive] = useState<ActiveSelection | null>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: STYLE_URL,
      center: [10, 50], // Europe-centric default
      zoom: 3,
      attributionControl: { compact: true },
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right');
    map.addControl(new maplibregl.FullscreenControl(), 'bottom-right');
    map.on('load', () => setMapReady(true));
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      setMapReady(false);
    };
  }, []);

  const { data: mapData } = useQuery({
    queryKey: ['map', 'entities'],
    queryFn: () => kgApi.mapEntities({ limit: 500 }),
    staleTime: 60_000,
  });

  // Build a sorted, deduplicated list of classes present in the result
  // set. Drives both the legend (chips with colors) and the visibility
  // checkboxes — no hand-maintained class list.
  const classBuckets = useMemo(() => {
    if (!mapData) return [] as { className: string; count: number }[];
    const counts = new Map<string, number>();
    for (const e of mapData.entities) {
      const key = shortClassName(e.className) || 'Other';
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([className, count]) => ({ className, count }))
      .sort((a, b) => b.count - a.count);
  }, [mapData]);

  // `visibleClasses === null` means "use the default selection" — once
  // we've seen the bucket list we materialise it to an explicit Set,
  // skipping the broad geographic categories so the initial view shows
  // the actual subjects of interest (projects, facilities, plants…)
  // rather than a country marker stuck in the middle of every country.
  // Subsequent renders leave the Set intact so user toggles persist.
  const [visibleClasses, setVisibleClasses] = useState<Set<string> | null>(null);

  useEffect(() => {
    if (visibleClasses !== null) return;
    if (classBuckets.length === 0) return;
    const next = new Set(
      classBuckets
        .map((b) => b.className)
        .filter((cls) => !DEFAULT_HIDDEN_CLASSES.has(cls)),
    );
    setVisibleClasses(next);
  }, [classBuckets, visibleClasses]);

  const visibleEntities = useMemo(() => {
    if (!mapData) return [] as MapEntity[];
    if (visibleClasses === null) {
      // First render before the bucket-driven init effect fires — apply
      // the same geographic-hidden default so we don't flash unwanted
      // markers for a frame.
      return mapData.entities.filter(
        (e) => !DEFAULT_HIDDEN_CLASSES.has(shortClassName(e.className) || 'Other'),
      );
    }
    return mapData.entities.filter((e) =>
      visibleClasses.has(shortClassName(e.className) || 'Other'),
    );
  }, [mapData, visibleClasses]);

  // Bucket entities by rounded coords (4 decimals ≈ 11m precision).
  // Same-address entities (e.g. multiple projects at one plant
  // location) end up under one marker with a count badge — analysts
  // open the cluster popup to see the full list instead of having
  // duplicates stacked under the topmost marker.
  const markerGroups = useMemo<MarkerGroup[]>(() => {
    const buckets = new Map<string, MapEntity[]>();
    for (const e of visibleEntities) {
      const key = `${e.lat.toFixed(4)},${e.lon.toFixed(4)}`;
      const existing = buckets.get(key);
      if (existing) existing.push(e);
      else buckets.set(key, [e]);
    }
    return Array.from(buckets.entries()).map(([key, entities]) => {
      const [lat, lon] = key.split(',').map(Number) as [number, number];
      return { lat, lon, entities };
    });
  }, [visibleEntities]);

  // Create the rich popup once. Its DOM content is `popupHost`; we
  // re-render React into it via createPortal (below) whenever the
  // active selection changes.
  //
  // Two non-obvious choices:
  //
  //   - `closeOnClick: false` — without it, a click on a cluster row
  //     races with maplibre's map-level click handler and closes the
  //     popup right after React schedules the swap to the single
  //     popup, so the analyst sees nothing.
  //   - No `popup.on('close', …)` listener. Since closing now only
  //     happens when we explicitly call setActive(null), the listener
  //     was redundant AND it was firing during the cluster→single
  //     transition (an internal maplibre lifecycle event sneaks
  //     through), wiping the new state. Lifecycle is now fully
  //     driven by React state.
  //
  // `POPUP_CONFIG_VERSION` is a dummy dep that lets us force a popup
  // recreate when its config (e.g. `closeOnClick`) changes — without
  // it, Vite HMR re-runs the component but keeps the live popup with
  // its original options, masking config fixes until a hard refresh.
  useEffect(() => {
    if (!mapReady) return;
    const popup = new maplibregl.Popup({
      offset: 14,
      closeButton: false,
      closeOnClick: false,
      maxWidth: '380px',
    }).setDOMContent(popupHost);
    popupRef.current = popup;
    return () => {
      popup.remove();
      popupRef.current = null;
    };
  }, [mapReady, popupHost, POPUP_CONFIG_VERSION]);

  // Render markers once both the map and the entities are ready. We
  // rebuild the marker set on every change rather than diffing — at
  // MVP volumes (≪1000 markers) the cost is negligible and the code
  // is simpler. Each marker now represents a coord-bucket; the count
  // badge surfaces when N>1 entities live there.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    for (const m of markersRef.current) m.remove();
    markersRef.current = [];
    for (const g of markerGroups) {
      const el = makeMarkerElement(g.entities, g.entities.length);
      el.addEventListener('click', (ev) => {
        ev.stopPropagation();
        if (g.entities.length === 1) {
          setActive({ kind: 'single', entity: g.entities[0]! });
        } else {
          setActive({
            kind: 'group',
            entities: g.entities,
            lat: g.lat,
            lon: g.lon,
          });
        }
      });
      const marker = new maplibregl.Marker({ element: el })
        .setLngLat([g.lon, g.lat])
        .addTo(map);
      markersRef.current.push(marker);
    }
    // Fit the viewport to the markers if they all sit in roughly one region.
    if (markerGroups.length > 0) {
      const bounds = new maplibregl.LngLatBounds();
      for (const g of markerGroups) bounds.extend([g.lon, g.lat]);
      map.fitBounds(bounds, { padding: 80, maxZoom: 6, duration: 600 });
    }
  }, [mapReady, markerGroups]);

  // Sync the maplibre popup with the active selection. Re-positioning
  // is handled by maplibre once the popup is added to the map.
  useEffect(() => {
    const map = mapRef.current;
    const popup = popupRef.current;
    if (!map || !popup) return;
    if (active) {
      const [lon, lat] =
        active.kind === 'single'
          ? [active.entity.lon, active.entity.lat]
          : [active.lon, active.lat];
      popup.setLngLat([lon, lat]).addTo(map);
    } else if (popup.isOpen()) {
      popup.remove();
    }
  }, [active]);

  const activeFilterCount = countActiveFilters(filters);
  const resultCount = visibleEntities.length;

  return (
    <div className="relative h-[calc(100vh-3.5rem)]">
      <div ref={containerRef} className="absolute inset-0" aria-label="ETT entity map" />

      {/* Top-left filters trigger */}
      <div className="absolute left-4 top-4 z-10 flex items-center gap-2">
        <Button variant="secondary" onClick={() => setFiltersOpen((v) => !v)}>
          <Filter className="mr-1 h-4 w-4" aria-hidden />
          {t('map.filters')}
          {activeFilterCount > 0 && (
            <Badge variant="accent" className="ml-2">
              {activeFilterCount}
            </Badge>
          )}
        </Button>
      </div>

      {/* Top-right search */}
      <div className="absolute right-4 top-4 z-10 w-72">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-slate-400" aria-hidden />
          <Input placeholder={t('map.searchPlaceholder')} className="pl-8" />
        </div>
      </div>

      {/* Bottom-centre results count */}
      <div className="absolute bottom-4 left-1/2 z-10 -translate-x-1/2 rounded-full bg-white/95 px-3 py-1 text-small text-slate-600 shadow">
        {resultCount === 0
          ? t('map.noResults')
          : t('map.results', { count: resultCount })}
      </div>

      <MapFilterPanel
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        filters={filters}
        onFiltersChange={setFilters}
      />

      {/* Interactive legend (bottom-left) — class checkboxes */}
      <Legend
        buckets={classBuckets}
        visible={visibleClasses}
        onToggle={(cls) => {
          setVisibleClasses((cur) => {
            const next = new Set(cur ?? classBuckets.map((b) => b.className));
            if (next.has(cls)) next.delete(cls);
            else next.add(cls);
            return next;
          });
        }}
        onAll={() => setVisibleClasses(new Set(classBuckets.map((b) => b.className)))}
        onNone={() => setVisibleClasses(new Set())}
      />

      {/* Marker popup content — rendered into maplibre's popup div via
          portal so React context (QueryClient, Router) stays in scope.
          Cluster popup lists co-located entities; click a row to drill
          into that entity's single-popup view with a Back affordance. */}
      {active &&
        createPortal(
          active.kind === 'group' ? (
            <MapClusterPopup
              entities={active.entities}
              lat={active.lat}
              lon={active.lon}
              onSelect={(entity) =>
                setActive({ kind: 'single', entity, siblings: active.entities })
              }
              onClose={() => setActive(null)}
            />
          ) : (
            <MapPopupContent
              entity={active.entity}
              onClose={() => setActive(null)}
              onBack={
                active.siblings
                  ? () =>
                      setActive({
                        kind: 'group',
                        entities: active.siblings!,
                        lat: active.entity.lat,
                        lon: active.entity.lon,
                      })
                  : undefined
              }
            />
          ),
          popupHost,
        )}
    </div>
  );
}

// Bump whenever a maplibre Popup option changes so HMR forces a
// recreate of the live popup instead of keeping the old config.
const POPUP_CONFIG_VERSION = 3;

// ─── Marker grouping types ───────────────────────────────────────

interface MarkerGroup {
  lat: number;
  lon: number;
  entities: MapEntity[];
}

type ActiveSelection =
  | { kind: 'single'; entity: MapEntity; siblings?: MapEntity[] }
  | { kind: 'group'; entities: MapEntity[]; lat: number; lon: number };

function countActiveFilters(filters: MapFilters): number {
  let n = 0;
  if (filters.statuses.length) n += 1;
  if (filters.technologies.length) n += 1;
  if (filters.geographies.length) n += 1;
  if (filters.minCapacity != null || filters.maxCapacity != null) n += 1;
  if (filters.dateFrom || filters.dateTo) n += 1;
  return n;
}

// ─── Marker helpers ──────────────────────────────────────────────

// Broad geographic categories that mostly act as container labels
// (a single dot in the middle of a country / region / municipality).
// We start with them unchecked so the map opens on the actual
// subjects of interest — analysts can still tick them back on from
// the legend whenever they want.
const DEFAULT_HIDDEN_CLASSES = new Set<string>([
  'Country',
  'Region',
  'Geography',
  'Location',
  'Municipality',
]);

// Dynamic palette — markers and legend bucket entities by the *short*
// suffix of the type IRI (e.g. `HydrogenProject`) and pick a color
// deterministically from a palette so the legend chip and the map dot
// always match without a hand-maintained mapping.
const PALETTE = [
  '#3b82f6', // blue-500
  '#10b981', // emerald-500
  '#f59e0b', // amber-500
  '#a855f7', // purple-500
  '#ef4444', // red-500
  '#06b6d4', // cyan-500
  '#f97316', // orange-500
  '#ec4899', // pink-500
  '#84cc16', // lime-500
  '#6366f1', // indigo-500
];

function shortClassName(iri: string): string {
  if (!iri) return '';
  const last = iri.split(/[#/]/).pop() ?? '';
  return last;
}

function colorForClass(className: string): string {
  const short = shortClassName(className) || 'Other';
  let h = 0;
  for (let i = 0; i < short.length; i++) h = (h * 31 + short.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length]!;
}

function makeMarkerElement(entities: MapEntity[], count: number): HTMLElement {
  const first = entities[0]!;
  // Color picked from the most-frequent class in the bucket — gives
  // the cluster a meaningful default colour without being arbitrary.
  const dominantClass = pickDominantClass(entities);
  const wrap = document.createElement('div');
  wrap.style.background = colorForClass(dominantClass);
  wrap.style.border = '2px solid #ffffff';
  wrap.style.boxShadow = '0 1px 3px rgba(15,23,42,0.4)';
  wrap.style.cursor = 'pointer';
  wrap.style.display = 'flex';
  wrap.style.alignItems = 'center';
  wrap.style.justifyContent = 'center';
  wrap.style.color = '#ffffff';
  wrap.style.fontWeight = '600';
  wrap.style.lineHeight = '1';
  if (count > 1) {
    // Slightly larger circle with the count inscribed so the badge is
    // legible without crowding nearby singletons.
    const size = Math.min(28, 16 + Math.log2(count) * 4);
    wrap.style.width = `${size}px`;
    wrap.style.height = `${size}px`;
    wrap.style.fontSize = '11px';
    wrap.textContent = count > 99 ? '99+' : String(count);
    wrap.title = `${count} entities at this location`;
  } else {
    wrap.style.width = '14px';
    wrap.style.height = '14px';
    wrap.title = first.label;
  }
  wrap.style.borderRadius = '50%';
  return wrap;
}

function pickDominantClass(entities: MapEntity[]): string {
  if (entities.length === 1) return entities[0]!.className;
  const counts = new Map<string, number>();
  for (const e of entities) {
    counts.set(e.className, (counts.get(e.className) ?? 0) + 1);
  }
  let best = entities[0]!.className;
  let bestCount = 0;
  for (const [cls, n] of counts) {
    if (n > bestCount) {
      best = cls;
      bestCount = n;
    }
  }
  return best;
}

interface LegendProps {
  buckets: { className: string; count: number }[];
  visible: Set<string> | null;
  onToggle: (cls: string) => void;
  onAll: () => void;
  onNone: () => void;
}

function Legend({ buckets, visible, onToggle, onAll, onNone }: LegendProps) {
  const { t } = useTranslation('app');
  const isVisible = (cls: string) =>
    visible === null ? !DEFAULT_HIDDEN_CLASSES.has(cls) : visible.has(cls);
  const allOn = visible !== null && buckets.every((b) => visible.has(b.className));
  const noneOn = visible !== null && buckets.every((b) => !visible.has(b.className));

  return (
    <div className="absolute bottom-4 left-4 z-10 max-h-80 w-64 overflow-y-auto rounded border border-slate-200 bg-white/95 p-2 text-small shadow">
      <div className="mb-1 flex items-center justify-between">
        <p className="font-medium text-slate-700">{t('map.legend.title')}</p>
        <div className="flex gap-1 text-small">
          <button
            type="button"
            onClick={onAll}
            disabled={allOn}
            className={cn(
              'rounded px-1 transition-colors',
              allOn ? 'text-slate-400' : 'text-accent-700 hover:underline',
            )}
          >
            {t('map.legend.all')}
          </button>
          <span className="text-slate-300">/</span>
          <button
            type="button"
            onClick={onNone}
            disabled={noneOn}
            className={cn(
              'rounded px-1 transition-colors',
              noneOn ? 'text-slate-400' : 'text-accent-700 hover:underline',
            )}
          >
            {t('map.legend.none')}
          </button>
        </div>
      </div>
      {buckets.length === 0 ? (
        <p className="text-slate-500">{t('map.legend.empty')}</p>
      ) : (
        <ul className="space-y-0.5">
          {buckets.map((b) => (
            <li key={b.className}>
              <label className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 hover:bg-slate-100">
                <input
                  type="checkbox"
                  checked={isVisible(b.className)}
                  onChange={() => onToggle(b.className)}
                  className="h-3 w-3"
                />
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ background: colorForClass(b.className) }}
                  aria-hidden
                />
                <span className="flex-1 truncate text-slate-700">{b.className}</span>
                <span className="text-slate-400">{b.count}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
